import { useMemo, useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlarmTypeLite, AttributeTypeLite, createAlarmRule, getAttributeTypes } from '@/api/alertsApi';

interface AddThresholdModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | null;
  alarmTypes: AlarmTypeLite[];
  onCreated: () => void;
}

export function AddThresholdModal({
  isOpen,
  onClose,
  projectId,
  alarmTypes,
  onCreated,
}: AddThresholdModalProps) {
  const [selectedAttributeTypeId, setSelectedAttributeTypeId] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [thresholdOperator, setThresholdOperator] = useState<'>' | '<' | '=' | '!=' | ''>('');
  const [thresholdValue, setThresholdValue] = useState<string>('');
  const [cooldownSeconds, setCooldownSeconds] = useState<string>('0');
  const [enabled, setEnabled] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const projectIri = projectId ? `/api/projects/${projectId}` : null;

  const { data: attributeTypes = [], isLoading: isLoadingAttributeTypes } = useQuery({
    queryKey: ['alarmRuleAttributeTypes'],
    queryFn: async () => {
      return await getAttributeTypes();
    },
    enabled: isOpen,
  });

  const alarmTypeId = useMemo(() => {
    const t = alarmTypes.find((x) => x.code === 'ATTRIBUTE_THRESHOLD');
    return t?.id ?? null;
  }, [alarmTypes]);

  const cooldownNum = useMemo(() => Number(cooldownSeconds), [cooldownSeconds]);
  const thresholdValueNum = useMemo(() => Number(thresholdValue), [thresholdValue]);

  const filteredAttributeTypes = useMemo(() => {
    if (!searchQuery.trim()) return attributeTypes;
    const query = searchQuery.toLowerCase().trim();
    return attributeTypes.filter((t: AttributeTypeLite) => {
      const code = (t.code || '').toLowerCase();
      const name = (t.name || '').toLowerCase();
      return code.includes(query) || name.includes(query);
    });
  }, [attributeTypes, searchQuery]);

  const selectedAttributeType = useMemo(() => {
    if (!selectedAttributeTypeId) return null;
    return attributeTypes.find((t) => t.id === selectedAttributeTypeId) || null;
  }, [attributeTypes, selectedAttributeTypeId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus input when dropdown opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const canCreate = useMemo(() => {
    if (!projectIri) return false;
    if (!alarmTypeId) return false;
    if (!selectedAttributeTypeId) return false;
    if (!thresholdOperator) return false;
    if (!Number.isFinite(thresholdValueNum)) return false;
    if (thresholdValue.trim() === '') return false;
    if (!Number.isFinite(cooldownNum) || cooldownNum < 0) return false;
    return true;
  }, [
    projectIri,
    alarmTypeId,
    selectedAttributeTypeId,
    thresholdOperator,
    thresholdValueNum,
    thresholdValue,
    cooldownNum,
  ]);

  const createMutation = useMutation({
    mutationFn: async () => {
      setFormError(null);
      if (!projectIri) throw new Error('Projet requis');
      if (!alarmTypeId) throw new Error('Type introuvable');
      if (!selectedAttributeTypeId) throw new Error('Type d\'attribut requis');
      if (!thresholdOperator) throw new Error('Opérateur requis');
      if (thresholdValue.trim() === '') throw new Error('Valeur seuil requise');

      return await createAlarmRule({
        project: projectIri,
        type: `/api/alarm_types/${alarmTypeId}`,
        scope: 'attribute_type',
        attributeType: `/api/attribute_types/${selectedAttributeTypeId}`,
        operator: thresholdOperator as '>' | '<' | '=' | '!=',
        thresholdValue: String(thresholdValueNum),
        cooldownSeconds: cooldownNum > 0 ? cooldownNum : null,
        enabled,
      });
    },
    onSuccess: () => {
      onCreated();
      onClose();
      setSelectedAttributeTypeId('');
      setSearchQuery('');
      setIsDropdownOpen(false);
      setThresholdOperator('');
      setThresholdValue('');
      setCooldownSeconds('0');
      setEnabled(true);
      setFormError(null);
    },
    onError: (e: any) => {
      const msg = e?.body?.detail || e?.message || 'Erreur lors de la création';
      setFormError(String(msg));
    },
  });

  const handleClose = () => {
    onClose();
    setSelectedAttributeTypeId('');
    setSearchQuery('');
    setIsDropdownOpen(false);
    setThresholdOperator('');
    setThresholdValue('');
    setCooldownSeconds('0');
    setEnabled(true);
    setFormError(null);
  };

  const handleSelectAttributeType = (type: AttributeTypeLite) => {
    setSelectedAttributeTypeId(type.id);
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg mx-4 bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
              tune
            </span>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Ajouter un seuil
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-[#1E293B] dark:hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <span className="material-icons-outlined" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!projectIri && (
            <div className="text-sm text-amber-600 dark:text-amber-400">
              Contexte projet requis pour créer un seuil.
            </div>
          )}

          {!alarmTypeId && (
            <div className="text-sm text-amber-600 dark:text-amber-400">
              Type d'alerte ATTRIBUTE_THRESHOLD introuvable côté API.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Type d'attribut <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <div
                onClick={() => {
                  if (!isLoadingAttributeTypes) {
                    setIsDropdownOpen(!isDropdownOpen);
                    if (!isDropdownOpen) {
                      setSearchQuery('');
                    }
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 cursor-pointer flex items-center justify-between ${
                  isDropdownOpen ? 'ring-2 ring-primary' : ''
                } ${isLoadingAttributeTypes ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className={selectedAttributeType ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}>
                  {selectedAttributeType
                    ? `${selectedAttributeType.code || `#${selectedAttributeType.id}`}${selectedAttributeType.name ? ` • ${selectedAttributeType.name}` : ''}`
                    : 'Choisir un type d\'attribut…'}
                </span>
                <span className="material-icons-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '20px' }}>
                  {isDropdownOpen ? 'expand_less' : 'expand_more'}
                </span>
              </div>

              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-slate-200 dark:border-[#1E293B]">
                    <div className="relative">
                      <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Rechercher par code ou nom…"
                        className="w-full px-3 py-2 pl-10 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                        <span className="material-icons-outlined" style={{ fontSize: '18px' }}>search</span>
                      </span>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {filteredAttributeTypes.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 text-center">
                        Aucun résultat trouvé
                      </div>
                    ) : (
                      filteredAttributeTypes.map((t: AttributeTypeLite) => (
                        <div
                          key={t.id}
                          onClick={() => handleSelectAttributeType(t)}
                          className={`px-4 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-[#1E293B] ${
                            selectedAttributeTypeId === t.id ? 'bg-primary/10 dark:bg-primary/20' : ''
                          }`}
                        >
                          <div className="text-sm text-slate-900 dark:text-white">
                            {t.code || `#${t.id}`}
                          </div>
                          {t.name && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {t.name}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                Opérateur <span className="text-red-500">*</span>
              </label>
              <select
                value={thresholdOperator}
                onChange={(e) => setThresholdOperator(e.target.value as '>' | '<' | '=' | '!=' | '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Choisir…</option>
                <option value=">">&gt;</option>
                <option value="<">&lt;</option>
                <option value="=">=</option>
                <option value="!=">!=</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                Valeur seuil <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={thresholdValue}
                onChange={(e) => setThresholdValue(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="0"
              />
              {thresholdValue.trim() === '' && (
                <div className="mt-1 text-xs text-red-500">Requis</div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Cooldown (secondes)
            </label>
            <input
              type="number"
              min={0}
              value={cooldownSeconds}
              onChange={(e) => setCooldownSeconds(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0"
            />
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Délai minimum entre deux alertes (0 = pas de cooldown)
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-[#1E293B]">
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Activée</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Activer / désactiver la règle</div>
            </div>
            <button
              type="button"
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enabled ? 'bg-primary' : 'bg-slate-400'
              }`}
              aria-pressed={enabled}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {formError && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              {formError}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-[#1E293B] flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-[#1E293B] transition-colors"
          >
            Annuler
          </button>

          <button
            disabled={!canCreate || createMutation.isPending}
            onClick={() => {
              if (!canCreate) return;
              createMutation.mutate();
            }}
            className={`px-4 py-2 rounded-lg text-white transition-colors ${
              canCreate && !createMutation.isPending ? 'bg-primary hover:bg-primary/90' : 'bg-slate-400 cursor-not-allowed'
            }`}
          >
            {createMutation.isPending ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}
